import "../UserCollectedData.css";
import React, { useState } from "react";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import { Button, Dialog, DialogActions, DialogTitle, IconButton, Tooltip } from "@mui/material";
import { GetSavedSearchResponse, SavedSearchType } from "@shared/publicportal/contracts/ISavedSearch";
import UserCollectedDataHelper, { CriteriaValues } from "../UserCollectedDataHelper";
import { Serializer } from "@shared/mirage/helpers/Serializer";
import { Link, useNavigate } from "react-router-dom";
import { EntityTableStateStorage } from "@mirage/components/entityTable/EntityTableStateStorage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlay, faPen, faTrashXmark } from "@fortawesome/pro-regular-svg-icons";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";

interface CriteriaTableProps {
    criteria: GetSavedSearchResponse[];
    onEditClicked: (criteria: GetSavedSearchResponse) => void;
    onDeleteClicked: (id: number) => Promise<void>;
}

const pp = new PublicPortalRoutingService();

const CriteriaTable: React.FC<CriteriaTableProps> = (props) => {
    const navigate = useNavigate();
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [searchToDelete, setSearchToDelete] = useState<GetSavedSearchResponse>({} as GetSavedSearchResponse);

    const closeModal = () => {
        setOpenDeleteConfirm(false);
    };

    return (
        <>
            <Dialog open={openDeleteConfirm} PaperProps={{ className: "delete-record-dialog" }}>
                <DialogTitle>
                    You are going to delete <b>{`${searchToDelete.searchName}`}</b>
                </DialogTitle>
                <DialogActions className="delete-record-dialog__actions">
                    <Button onClick={closeModal} autoFocus variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={async () => await handleConfirmDelete()} color="error" variant="outlined">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <TableContainer component={Paper}>
                <Table className="criteria-table" aria-label="simple table" size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell className="column-header-cell column-header-cell-narrow ">Actions</TableCell>
                            <TableCell className="column-header-cell column-header-cell-narrow ">Search Name</TableCell>
                            <TableCell className="column-header-cell">Criteria</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{getTableRows()}</TableBody>
                </Table>
            </TableContainer>
        </>
    );

    function getTableRows() {
        return props.criteria.map((crit) => {
            const criteriaValues = JSON.parse(crit.jsonSearchParameters) as CriteriaValues;
            delete criteriaValues.searchType;
            const [key, url] = getSearchUrl(crit);
            return (
                <TableRow key={crit.id}>
                    <TableCell>{getActions(crit)}</TableCell>
                    <TableCell>
                        <Link to={url} onClick={() => EntityTableStateStorage.clear(key)}>
                            {crit.searchName}
                        </Link>
                    </TableCell>
                    <TableCell>{getCriteria(criteriaValues, crit.searchType)}</TableCell>
                </TableRow>
            );
        });
    }

    function getActions(criteria: GetSavedSearchResponse) {
        return (
            <>
                <Tooltip title="Run Search" placement="top">
                    <IconButton onClick={() => handleRunSearch(criteria)}>
                        <FontAwesomeIcon icon={faCirclePlay} color={PublicPortalTheme.palette.success.main} style={PublicPortalTheme.icons.sizeM} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Edit Search" placement="top">
                    <IconButton onClick={() => handleEditSavedSearch(criteria)}>
                        <FontAwesomeIcon icon={faPen} color={PublicPortalTheme.palette.info.main} style={PublicPortalTheme.icons.sizeM} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete Search" placement="top">
                    <IconButton onClick={() => handleDeleteClicked(criteria)}>
                        <FontAwesomeIcon icon={faTrashXmark} color={PublicPortalTheme.palette.error.main} style={PublicPortalTheme.icons.sizeM} />
                    </IconButton>
                </Tooltip>
            </>
        );
    }
    function formatGeometry(value: string) {
        const json = JSON.parse(value) as any;
        if (json.features) {
            const topFeature = json.features[0].geometry;
            return topFeature.type;
        }
        return "empty";
    }
    function getCriteria(searchValues: CriteriaValues, searchType: SavedSearchType) {
        const criteriaArr: JSX.Element[] = [];

        for (const [key, value] of Object.entries(searchValues)) {
            const isValueDate = UserCollectedDataHelper.isDate(key);
            const isGeometry = UserCollectedDataHelper.isGeometry(key);
            let formattedValue = isValueDate ? DateFormatter.formatToDate(value) : value;
            formattedValue = isGeometry ? formatGeometry(value) : formattedValue;
            const label = getCriteriaLabel(key, searchType);
            const criteriaElement = (
                <span key={`${key}-${value}`} className="criteria_element">
                    <b>{label}:</b> {formattedValue}
                </span>
            );

            criteriaArr.push(criteriaElement);
        }

        return criteriaArr;
    }

    function getCriteriaLabel(stringValue: string, searchType: SavedSearchType) {
        stringValue = UserCollectedDataHelper.mapLabel(stringValue, searchType);
        const firstLetterCapitalized = stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
        const valueContainsLowercase = /[a-z]/.test(stringValue);
        if (valueContainsLowercase) {
            return firstLetterCapitalized.replace(/([A-Z])/g, " $1").trim();
        }
        return firstLetterCapitalized;
    }

    function getSearchUrl(criteria: GetSavedSearchResponse) {
        const rawParams = criteria.jsonSearchParameters;
        const parsedParams = rawParams && JSON.parse(rawParams);
        const searchParams = Serializer.toQueryParamsNotEmptyOnly(parsedParams);
        return pp.SearchRoute(criteria.searchType, searchParams);
    }

    async function handleConfirmDelete() {
        closeModal();
        await props.onDeleteClicked(searchToDelete.id);
    }

    function handleDeleteClicked(criteria: GetSavedSearchResponse) {
        setSearchToDelete(criteria);
        setOpenDeleteConfirm(true);
    }

    function handleRunSearch(criteria: GetSavedSearchResponse) {
        const url = getSearchUrl(criteria);
        EntityTableStateStorage.clear(criteria.searchType);
        navigate(url);
    }

    function handleEditSavedSearch(criteria: GetSavedSearchResponse) {
        props.onEditClicked(criteria);
    }
};
export default CriteriaTable;
